import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
    Clock, 
    Unlock, 
    Package,
    Building2,
    AlertTriangle,
    ShoppingCart,
    History,
    Search,
    RefreshCw,
    Users,
    ChevronDown,
    ChevronRight,
    Tag,
    Percent,
    TrendingDown,
    Check,
    X,
    Gift,
    Ban,
    Euro,
    Calculator,
    RotateCcw,
    Plus,
    Trash2,
    Edit3,
    Barcode,
    UserPlus,
    Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminReservations = () => {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('live');
    const [reservationsByClient, setReservationsByClient] = useState([]);
    const [cartHistory, setCartHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [releasing, setReleasing] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedClients, setExpandedClients] = useState({});
    const [stats, setStats] = useState({ total_reservations: 0, total_clients: 0 });

    // Discount modal state
    const [discountModalOpen, setDiscountModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [cartDetails, setCartDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [discounts, setDiscounts] = useState({}); // {product_id: amount}
    const [globalDiscount, setGlobalDiscount] = useState(0);
    const [applyingDiscount, setApplyingDiscount] = useState(false);
    const [cancellingOffer, setCancellingOffer] = useState(false);

    // Relaunch cart state
    const [relaunching, setRelaunching] = useState({});
    const [relaunchResult, setRelaunchResult] = useState(null);
    const [relaunchModalOpen, setRelaunchModalOpen] = useState(false);

    // Cart management state
    const [approvedClients, setApprovedClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState('');
    const [managedCart, setManagedCart] = useState(null);
    const [imeiInput, setImeiInput] = useState('');
    const [bulkImeiInput, setBulkImeiInput] = useState('');
    const [bulkMode, setBulkMode] = useState(false);
    const [addingProduct, setAddingProduct] = useState(false);
    const [addingBulk, setAddingBulk] = useState(false);
    const [bulkResults, setBulkResults] = useState(null);
    const [removingProduct, setRemovingProduct] = useState({});
    const [loadingManagedCart, setLoadingManagedCart] = useState(false);

    const fetchReservationsByClient = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/reservations/by-client`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReservationsByClient(response.data.clients || []);
            setStats({
                total_reservations: response.data.total_reservations,
                total_clients: response.data.total_clients
            });
            // Auto-expand all clients by default
            const expanded = {};
            response.data.clients?.forEach(c => {
                expanded[c.client?.id] = true;
            });
            setExpandedClients(expanded);
        } catch (error) {
            console.error('Fetch reservations error:', error);
            toast.error('Erreur lors du chargement des réservations');
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchCartHistory = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/carts/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCartHistory(response.data.carts || []);
        } catch (error) {
            console.error('Fetch cart history error:', error);
        }
    }, [token]);

    // Fetch approved clients for cart management
    const fetchApprovedClients = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/clients/approved`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setApprovedClients(response.data.clients || []);
        } catch (error) {
            console.error('Fetch approved clients error:', error);
        }
    }, [token]);

    // Fetch managed cart details
    const fetchManagedCart = async (userId) => {
        if (!userId) {
            setManagedCart(null);
            return;
        }
        setLoadingManagedCart(true);
        try {
            const response = await axios.get(`${API_URL}/admin/carts/${userId}/details`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setManagedCart(response.data);
        } catch (error) {
            console.error('Fetch managed cart error:', error);
            toast.error('Erreur lors du chargement du panier');
        } finally {
            setLoadingManagedCart(false);
        }
    };

    // Add product by IMEI
    const handleAddByImei = async () => {
        if (!selectedClientId || !imeiInput.trim()) {
            toast.error('Veuillez sélectionner un client et entrer un IMEI');
            return;
        }
        
        setAddingProduct(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/carts/${selectedClientId}/add-by-imei`,
                { imei: imeiInput.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(`${response.data.product.brand} ${response.data.product.model} ajouté au panier`);
            setImeiInput('');
            setManagedCart(prev => ({
                ...prev,
                cart: response.data.cart
            }));
            
            // Refresh other tabs data
            fetchReservationsByClient();
            fetchCartHistory();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout du produit');
        } finally {
            setAddingProduct(false);
        }
    };

    // Add multiple products by IMEI (bulk) with optional final prices
    const handleAddBulkImei = async () => {
        if (!selectedClientId || !bulkImeiInput.trim()) {
            toast.error('Veuillez sélectionner un client et coller des IMEI');
            return;
        }
        
        // Parse lines from textarea
        // Supports formats:
        // - IMEI only: "123456789012345"
        // - IMEI;Price: "123456789012345;200"
        // - IMEI,Price: "123456789012345,200"
        // - IMEI TAB Price: "123456789012345	200"
        // - IMEI SPACE Price: "123456789012345 200"
        const lines = bulkImeiInput
            .split(/[\n]+/)
            .map(line => line.trim())
            .filter(line => line.length > 0);
        
        const items = lines.map(line => {
            // Split by semicolon, comma, tab, or space(s) to get IMEI and optional price
            const parts = line.split(/[;,\t\s]+/).map(p => p.trim());
            const imei = parts[0];
            const finalPrice = parts[1] ? parseFloat(parts[1]) : null;
            return { imei, final_price: finalPrice };
        }).filter(item => item.imei && item.imei.length > 0);
        
        if (items.length === 0) {
            toast.error('Aucun IMEI valide détecté');
            return;
        }
        
        setAddingBulk(true);
        setBulkResults(null);
        
        try {
            const response = await axios.post(
                `${API_URL}/admin/carts/${selectedClientId}/add-bulk-imei`,
                { items },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const { added, errors, cart } = response.data;
            
            // Show results
            setBulkResults({ added, errors });
            
            if (added.length > 0) {
                toast.success(`${added.length} produit(s) ajouté(s) au panier`);
            }
            if (errors.length > 0) {
                toast.warning(`${errors.length} IMEI non ajouté(s)`);
            }
            
            // Clear input only for successful adds
            if (added.length > 0 && errors.length === 0) {
                setBulkImeiInput('');
            }
            
            // Update cart
            setManagedCart(prev => ({
                ...prev,
                cart
            }));
            
            // Refresh other tabs data
            fetchReservationsByClient();
            fetchCartHistory();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout en masse');
        } finally {
            setAddingBulk(false);
        }
    };

    // Remove product from managed cart
    const handleRemoveFromManagedCart = async (productId) => {
        setRemovingProduct(prev => ({ ...prev, [productId]: true }));
        try {
            const response = await axios.delete(
                `${API_URL}/admin/carts/${selectedClientId}/remove/${productId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success('Produit retiré du panier');
            setManagedCart(prev => ({
                ...prev,
                cart: response.data.cart
            }));
            
            // Refresh other tabs data
            fetchReservationsByClient();
            fetchCartHistory();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du retrait du produit');
        } finally {
            setRemovingProduct(prev => ({ ...prev, [productId]: false }));
        }
    };

    // Handle client selection change
    const handleClientSelect = (clientId) => {
        setSelectedClientId(clientId);
        setImeiInput('');
        if (clientId) {
            fetchManagedCart(clientId);
        } else {
            setManagedCart(null);
        }
    };

    // Load cart from history into management tab
    const handleEditCart = (cart) => {
        setSelectedClientId(cart.user_id);
        setActiveTab('manage');
        fetchManagedCart(cart.user_id);
    };

    const fetchCartDetails = async (userId) => {
        setLoadingDetails(true);
        try {
            const response = await axios.get(`${API_URL}/admin/reservations/${userId}/cart-details`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCartDetails(response.data);
            
            // Initialize discounts from existing discount data
            const existingDiscounts = {};
            response.data.items?.forEach(item => {
                if (item.item_discount > 0) {
                    existingDiscounts[item.id] = item.item_discount;
                }
            });
            setDiscounts(existingDiscounts);
            setGlobalDiscount(response.data.totals?.global_discount || 0);
        } catch (error) {
            console.error('Fetch cart details error:', error);
            toast.error('Erreur lors du chargement des détails');
        } finally {
            setLoadingDetails(false);
        }
    };

    // Relaunch cart function
    const handleRelaunchCart = async (userId, clientInfo) => {
        setRelaunching(prev => ({ ...prev, [userId]: true }));
        try {
            const response = await axios.post(`${API_URL}/admin/carts/${userId}/relaunch`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setRelaunchResult({
                ...response.data,
                clientInfo
            });
            setRelaunchModalOpen(true);
            
            if (response.data.success) {
                toast.success(response.data.message);
                // Refresh data
                fetchCartHistory();
                fetchReservationsByClient();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Relaunch cart error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors du relancement');
        } finally {
            setRelaunching(prev => ({ ...prev, [userId]: false }));
        }
    };

    useEffect(() => {
        fetchReservationsByClient();
        fetchCartHistory();
        fetchApprovedClients();
        
        // Refresh every 30 seconds
        const interval = setInterval(() => {
            fetchReservationsByClient();
            fetchCartHistory();
        }, 30000);
        return () => clearInterval(interval);
    }, [fetchReservationsByClient, fetchCartHistory, fetchApprovedClients]);

    const handleRelease = async (productId) => {
        setReleasing(prev => ({ ...prev, [productId]: true }));
        try {
            await axios.post(
                `${API_URL}/admin/reservations/${productId}/release`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Réservation libérée');
            fetchReservationsByClient();
        } catch (error) {
            toast.error('Erreur lors de la libération');
        } finally {
            setReleasing(prev => ({ ...prev, [productId]: false }));
        }
    };

    const handleOpenDiscountModal = (clientData) => {
        setSelectedClient(clientData);
        setDiscountModalOpen(true);
        // Use client_id from response, fallback to client?.id
        const userId = clientData.client_id || clientData.client?.id;
        if (userId) {
            fetchCartDetails(userId);
        } else {
            toast.error('Impossible d\'identifier le client');
        }
    };

    const handleApplyDiscount = async () => {
        if (!selectedClient) return;
        
        setApplyingDiscount(true);
        try {
            // Build discount items array
            const items = Object.entries(discounts)
                .filter(([_, amount]) => amount > 0)
                .map(([product_id, discount_amount]) => ({ product_id, discount_amount }));
            
            // Use client_id from response, fallback to client?.id
            const userId = selectedClient.client_id || selectedClient.client?.id;
            if (!userId) {
                toast.error('Impossible d\'identifier le client');
                return;
            }
            
            await axios.post(
                `${API_URL}/admin/reservations/${userId}/apply-discount`,
                {
                    items,
                    global_discount: globalDiscount
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const totalDiscount = Object.values(discounts).reduce((a, b) => a + b, 0) + globalDiscount;
            toast.success(`Remise de ${totalDiscount}€ appliquée - Offre négociée active`);
            setDiscountModalOpen(false);
            fetchReservationsByClient();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'application de la remise');
        } finally {
            setApplyingDiscount(false);
        }
    };

    const handleCancelOffer = async () => {
        if (!selectedClient) return;
        
        setCancellingOffer(true);
        try {
            // Use client_id from response, fallback to client?.id
            const userId = selectedClient.client_id || selectedClient.client?.id;
            if (!userId) {
                toast.error('Impossible d\'identifier le client');
                return;
            }
            
            await axios.delete(
                `${API_URL}/admin/reservations/${userId}/cancel-offer`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success('Offre annulée - Réservation remise à 10 minutes');
            setDiscountModalOpen(false);
            fetchReservationsByClient();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
        } finally {
            setCancellingOffer(false);
        }
    };

    const getTimeRemaining = (expiresAt, hasNegotiatedDiscount) => {
        if (hasNegotiatedDiscount) return 'Illimité';
        
        const now = new Date();
        const expires = new Date(expiresAt);
        const diff = expires - now;
        
        if (diff <= 0) return 'Expiré';
        
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const isExpiringSoon = (expiresAt) => {
        const now = new Date();
        const expires = new Date(expiresAt);
        const diff = expires - now;
        return diff < 120000; // Less than 2 minutes
    };

    const toggleClientExpand = (clientId) => {
        setExpandedClients(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
    };

    // Filter clients by search term
    const filteredClients = reservationsByClient.filter(client => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            client.client?.company_name?.toLowerCase().includes(term) ||
            client.client?.email?.toLowerCase().includes(term) ||
            client.items?.some(item => 
                item.model?.toLowerCase().includes(term) ||
                item.imei?.includes(term)
            )
        );
    });

    const filteredHistory = cartHistory.filter(cart => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            cart.client?.company_name?.toLowerCase().includes(term) ||
            cart.client?.email?.toLowerCase().includes(term)
        );
    });

    const [cleaningExpired, setCleaningExpired] = useState(false);

    const handleCleanExpired = async () => {
        setCleaningExpired(true);
        try {
            const response = await axios.post(`${API_URL}/admin/reservations/clean-expired`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Nettoyage terminé: ${response.data.expired_cleaned} expirées, ${response.data.stuck_cleaned} bloquées`);
            fetchReservationsByClient();
            fetchCartHistory();
        } catch (error) {
            toast.error('Erreur lors du nettoyage');
        } finally {
            setCleaningExpired(false);
        }
    };

    // Calculate totals with discounts for the modal
    const calculateModalTotals = () => {
        if (!cartDetails) return { original: 0, discounted: 0, margin: 0, marginPct: 0, totalVat: 0, marginAfterVat: 0 };
        
        const items = cartDetails.items || [];
        const totalCost = items.reduce((sum, item) => sum + (item.erp_cost || 0), 0);
        const totalOriginal = items.reduce((sum, item) => sum + (item.original_price || 0), 0);
        const totalItemDiscounts = Object.values(discounts).reduce((sum, d) => sum + (parseFloat(d) || 0), 0);
        const totalDiscounted = totalOriginal - totalItemDiscounts - (parseFloat(globalDiscount) || 0);
        const margin = totalDiscounted - totalCost;
        const marginPct = totalDiscounted > 0 ? (margin / totalDiscounted * 100) : 0;
        
        // Calculate total VAT for all items
        let totalVat = 0;
        items.forEach(item => {
            const itemDiscount = parseFloat(discounts[item.id]) || 0;
            const finalPrice = item.original_price - itemDiscount;
            const itemMargin = finalPrice - (item.erp_cost || 0);
            const vatRegime = item.vat_regime || 'margin';
            
            if (vatRegime === 'standard') {
                totalVat += finalPrice * 0.20;
            } else {
                totalVat += itemMargin > 0 ? itemMargin * 20 / 120 : 0;
            }
        });
        
        // Apply proportional global discount to VAT calculation
        const marginAfterVat = margin - totalVat;
        
        return {
            original: totalOriginal,
            cost: totalCost,
            itemDiscounts: totalItemDiscounts,
            globalDiscount: parseFloat(globalDiscount) || 0,
            totalDiscount: totalItemDiscounts + (parseFloat(globalDiscount) || 0),
            discounted: totalDiscounted,
            margin,
            marginPct,
            totalVat,
            marginAfterVat
        };
    };

    const totals = calculateModalTotals();

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                            Réservations & Paniers
                        </h1>
                        <p className="text-slate-600 mt-1">
                            Suivi en temps réel des paniers clients
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button 
                            variant="outline" 
                            onClick={handleCleanExpired}
                            disabled={cleaningExpired}
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        >
                            <Unlock className="w-4 h-4 mr-2" />
                            {cleaningExpired ? 'Nettoyage...' : 'Nettoyer expirées'}
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={() => { fetchReservationsByClient(); fetchCartHistory(); }}
                            data-testid="refresh-reservations"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Actualiser
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <Card className="border-blue-200 bg-blue-50">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-blue-700">{stats.total_reservations}</p>
                                    <p className="text-xs text-blue-600">Produits réservés</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-green-200 bg-green-50">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <Users className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-green-700">{stats.total_clients}</p>
                                    <p className="text-xs text-green-600">Clients actifs</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-amber-200 bg-amber-50">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-100 rounded-lg">
                                    <History className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-amber-700">{cartHistory.length}</p>
                                    <p className="text-xs text-amber-600">Paniers en base</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-purple-200 bg-purple-50">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                    <Gift className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-purple-700">
                                        {reservationsByClient.filter(c => c.has_negotiated_discount).length}
                                    </p>
                                    <p className="text-xs text-purple-600">Offres négociées</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Search */}
                <div className="mb-6">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="text"
                            placeholder="Rechercher client, modèle, IMEI..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="live" className="flex items-center gap-2">
                            <ShoppingCart className="w-4 h-4" />
                            Réservations Live
                            {stats.total_reservations > 0 && (
                                <Badge className="bg-blue-500 text-white ml-1">{stats.total_reservations}</Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="history" className="flex items-center gap-2">
                            <History className="w-4 h-4" />
                            Historique Paniers
                        </TabsTrigger>
                        <TabsTrigger value="manage" className="flex items-center gap-2">
                            <Edit3 className="w-4 h-4" />
                            Gestion Paniers
                        </TabsTrigger>
                    </TabsList>

                    {/* Live Reservations Tab */}
                    <TabsContent value="live">
                        {loading ? (
                            <div className="space-y-4">
                                {[...Array(3)].map((_, i) => (
                                    <Card key={i} className="border-slate-200">
                                        <CardContent className="p-6">
                                            <div className="animate-pulse space-y-4">
                                                <div className="h-6 bg-slate-200 rounded w-1/3"></div>
                                                <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : filteredClients.length === 0 ? (
                            <div className="text-center py-16">
                                <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                                <h2 className="text-xl font-semibold text-slate-900 mb-2">Aucune réservation active</h2>
                                <p className="text-slate-600">Tous les produits sont disponibles</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {filteredClients.map((clientData) => (
                                    <Card 
                                        key={clientData.client?.id || 'unknown'} 
                                        className={`border-slate-200 overflow-hidden ${
                                            clientData.has_negotiated_discount 
                                                ? 'border-purple-300 bg-purple-50/30 ring-2 ring-purple-200' 
                                                : ''
                                        }`}
                                    >
                                        {/* Client Header */}
                                        <div 
                                            className={`p-4 border-b border-slate-200 cursor-pointer transition-colors ${
                                                clientData.has_negotiated_discount
                                                    ? 'bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-150'
                                                    : 'bg-slate-50 hover:bg-slate-100'
                                            }`}
                                            onClick={() => toggleClientExpand(clientData.client?.id)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    {expandedClients[clientData.client?.id] ? (
                                                        <ChevronDown className="w-5 h-5 text-slate-400" />
                                                    ) : (
                                                        <ChevronRight className="w-5 h-5 text-slate-400" />
                                                    )}
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                        clientData.has_negotiated_discount ? 'bg-purple-100' : 'bg-blue-100'
                                                    }`}>
                                                        <Building2 className={`w-5 h-5 ${
                                                            clientData.has_negotiated_discount ? 'text-purple-600' : 'text-blue-600'
                                                        }`} />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="font-semibold text-slate-900">
                                                                {clientData.client?.company_name || 'Client inconnu'}
                                                            </h3>
                                                            {clientData.has_negotiated_discount && (
                                                                <Badge className="bg-purple-500 text-white">
                                                                    <Gift className="w-3 h-3 mr-1" />
                                                                    Offre négociée
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-slate-500">
                                                            {clientData.client?.email}
                                                        </p>
                                                        {clientData.has_negotiated_discount && clientData.discount_applied_by && (
                                                            <p className="text-xs text-purple-600 mt-0.5">
                                                                Par {clientData.discount_applied_by} • Remise: {clientData.total_discount}€
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="text-right">
                                                        <p className="text-lg font-bold text-slate-900">
                                                            {clientData.has_negotiated_discount && clientData.total_discount > 0 ? (
                                                                <>
                                                                    <span className="line-through text-slate-400 text-sm mr-2">
                                                                        {clientData.total_ht_brut?.toFixed(2)}€
                                                                    </span>
                                                                    {clientData.total_ht?.toFixed(2)} € HT
                                                                </>
                                                            ) : (
                                                                `${clientData.total_ht?.toFixed(2)} € HT`
                                                            )}
                                                        </p>
                                                        <p className="text-sm text-slate-500">
                                                            {clientData.item_count} article{clientData.item_count > 1 ? 's' : ''}
                                                        </p>
                                                    </div>
                                                    <Badge className={clientData.has_negotiated_discount 
                                                        ? "bg-purple-100 text-purple-700" 
                                                        : "bg-green-100 text-green-700"
                                                    }>
                                                        {clientData.has_negotiated_discount ? (
                                                            <>
                                                                <Check className="w-3 h-3 mr-1" />
                                                                Illimité
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span className="relative flex h-2 w-2 mr-2">
                                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                                                </span>
                                                                En cours
                                                            </>
                                                        )}
                                                    </Badge>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={(e) => { e.stopPropagation(); handleOpenDiscountModal(clientData); }}
                                                        className={clientData.has_negotiated_discount 
                                                            ? "border-purple-300 text-purple-700 hover:bg-purple-100"
                                                            : "border-blue-300 text-blue-700 hover:bg-blue-100"
                                                        }
                                                    >
                                                        <Tag className="w-4 h-4 mr-1" />
                                                        {clientData.has_negotiated_discount ? 'Voir offre' : 'Remise'}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Client Items */}
                                        {expandedClients[clientData.client?.id] && (
                                            <CardContent className="p-0">
                                                <div className="divide-y divide-slate-100">
                                                    {clientData.items?.map((item) => {
                                                        const itemDiscount = clientData.discount_items?.[item.id] || 0;
                                                        return (
                                                            <div 
                                                                key={item.id}
                                                                className={`p-4 flex items-center justify-between ${
                                                                    !clientData.has_negotiated_discount && isExpiringSoon(item.reserved_until) 
                                                                        ? 'bg-red-50' 
                                                                        : ''
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-4">
                                                                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                                        <Package className="w-5 h-5 text-slate-400" />
                                                                    </div>
                                                                    <div>
                                                                        <p className="font-medium text-slate-900">
                                                                            {item.brand} {item.model}
                                                                        </p>
                                                                        <p className="text-sm text-slate-500 font-mono">
                                                                            {item.storage_capacity} GB • IMEI: {item.imei}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-4">
                                                                    <div className="text-right">
                                                                        {itemDiscount > 0 ? (
                                                                            <>
                                                                                <p className="font-bold text-purple-700">
                                                                                    {(item.price - itemDiscount).toFixed(2)} €
                                                                                </p>
                                                                                <p className="text-xs text-slate-400 line-through">
                                                                                    {item.price?.toFixed(2)} €
                                                                                </p>
                                                                            </>
                                                                        ) : (
                                                                            <p className="font-bold text-slate-900">
                                                                                {item.price?.toFixed(2)} €
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-2 min-w-[100px] justify-end">
                                                                        {clientData.has_negotiated_discount ? (
                                                                            <Badge className="bg-purple-100 text-purple-700">
                                                                                <Check className="w-3 h-3 mr-1" />
                                                                                Illimité
                                                                            </Badge>
                                                                        ) : (
                                                                            <>
                                                                                <Clock className={`w-4 h-4 ${isExpiringSoon(item.reserved_until) ? 'text-red-500' : 'text-slate-400'}`} />
                                                                                <span className={`text-sm font-mono ${isExpiringSoon(item.reserved_until) ? 'text-red-600 font-bold' : 'text-slate-600'}`}>
                                                                                    {getTimeRemaining(item.reserved_until, false)}
                                                                                </span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={(e) => { e.stopPropagation(); handleRelease(item.id); }}
                                                                        disabled={releasing[item.id]}
                                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                    >
                                                                        <Unlock className="w-4 h-4 mr-1" />
                                                                        Libérer
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </CardContent>
                                        )}
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Cart History Tab */}
                    <TabsContent value="history">
                        {filteredHistory.length === 0 ? (
                            <div className="text-center py-16">
                                <History className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                                <h2 className="text-xl font-semibold text-slate-900 mb-2">Aucun panier enregistré</h2>
                                <p className="text-slate-600">L&apos;historique des paniers apparaîtra ici</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {filteredHistory.map((cart, index) => (
                                    <Card 
                                        key={cart.user_id || index} 
                                        className={`border-slate-200 ${cart.is_active ? 'border-green-300 bg-green-50/30' : ''}`}
                                    >
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cart.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
                                                        <Building2 className={`w-5 h-5 ${cart.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="font-semibold text-slate-900">
                                                                {cart.client?.company_name || 'Client inconnu'}
                                                            </h3>
                                                            {cart.is_active && (
                                                                <Badge className="bg-green-100 text-green-700 text-xs">
                                                                    Actif
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-slate-500">
                                                            {cart.client?.email}
                                                        </p>
                                                        {cart.updated_at && (
                                                            <p className="text-xs text-slate-400 mt-1">
                                                                <Clock className="w-3 h-3 inline mr-1" />
                                                                {new Date(cart.updated_at).toLocaleDateString('fr-FR', {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                                {cart.is_historical && (
                                                                    <span className="ml-2 text-amber-500">(archivé)</span>
                                                                )}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="text-right">
                                                        <p className="font-bold text-slate-900">
                                                            {cart.total_ht?.toFixed(2)} € HT
                                                        </p>
                                                        <p className="text-sm text-slate-500">
                                                            {cart.item_count} article{cart.item_count > 1 ? 's' : ''}
                                                            {cart.active_count > 0 && (
                                                                <span className="text-green-600 ml-2">
                                                                    ({cart.active_count} réservé{cart.active_count > 1 ? 's' : ''})
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                    
                                                    {/* Relaunch Button */}
                                                    {!cart.is_active && cart.item_count > 0 && (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleRelaunchCart(cart.user_id, cart.client)}
                                                            disabled={relaunching[cart.user_id]}
                                                            className="bg-blue-600 hover:bg-blue-700 text-white"
                                                        >
                                                            {relaunching[cart.user_id] ? (
                                                                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                                            ) : (
                                                                <RotateCcw className="w-4 h-4 mr-1" />
                                                            )}
                                                            Relancer
                                                        </Button>
                                                    )}
                                                    
                                                    {/* Edit Button */}
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleEditCart(cart)}
                                                        className="border-slate-300"
                                                    >
                                                        <Edit3 className="w-4 h-4 mr-1" />
                                                        Modifier
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {/* Cart Items Preview with IMEI */}
                                            {cart.items && cart.items.length > 0 && (
                                                <div className="mt-4 pt-4 border-t border-slate-200">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <p className="text-xs text-slate-500 font-medium">IMEI des téléphones :</p>
                                                        <button
                                                            onClick={() => {
                                                                const imeis = cart.items
                                                                    .map(item => item.imei || item.imei_1)
                                                                    .filter(Boolean)
                                                                    .join('\n');
                                                                navigator.clipboard.writeText(imeis)
                                                                    .then(() => toast.success(`${imeis.split('\n').length} IMEI copiés — collez-les dans "Gestion Paniers"`))
                                                                    .catch(() => toast.error('Impossible de copier'));
                                                            }}
                                                            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-0.5 rounded-lg transition-colors"
                                                            data-testid="copy-imei-list-btn"
                                                        >
                                                            <Copy className="w-3 h-3" />
                                                            Copier les IMEI
                                                        </button>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {cart.items.slice(0, 8).map((item, idx) => (
                                                            <Badge 
                                                                key={idx} 
                                                                variant="outline" 
                                                                className="text-xs font-mono bg-slate-50"
                                                                title={`${item.brand} ${item.model} ${item.storage_capacity}GB`}
                                                            >
                                                                {item.imei || item.imei_1 || 'N/A'}
                                                            </Badge>
                                                        ))}
                                                        {cart.items.length > 8 && (
                                                            <Badge variant="outline" className="text-xs text-slate-400">
                                                                +{cart.items.length - 8} autres
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Cart Management Tab */}
                    <TabsContent value="manage">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Left: Client Selection & IMEI Input */}
                            <div className="lg:col-span-1 space-y-4">
                                <Card className="border-slate-200">
                                    <CardContent className="p-4 space-y-4">
                                        <div>
                                            <label className="text-sm font-medium text-slate-700 mb-2 block">
                                                Sélectionner un client
                                            </label>
                                            <Select value={selectedClientId} onValueChange={handleClientSelect}>
                                                <SelectTrigger className="w-full" data-testid="client-select">
                                                    <SelectValue placeholder="Choisir un client..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {approvedClients.map(client => (
                                                        <SelectItem key={client.id} value={client.id}>
                                                            <div className="flex items-center gap-2">
                                                                <Building2 className="w-4 h-4 text-slate-400" />
                                                                <span className="font-medium">{client.company_name}</span>
                                                                <span className="text-slate-400 text-xs">({client.email})</span>
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {selectedClientId && (
                                            <div className="pt-4 border-t border-slate-200">
                                                <div className="flex items-center justify-between mb-2">
                                                    <label className="text-sm font-medium text-slate-700">
                                                        <Barcode className="w-4 h-4 inline mr-1" />
                                                        Ajouter par IMEI
                                                    </label>
                                                    <button
                                                        onClick={() => {
                                                            setBulkMode(!bulkMode);
                                                            setBulkResults(null);
                                                        }}
                                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                                    >
                                                        {bulkMode ? '← Mode simple' : 'Mode liste (Excel) →'}
                                                    </button>
                                                </div>
                                                
                                                {!bulkMode ? (
                                                    // Single IMEI mode
                                                    <>
                                                        <div className="flex gap-2">
                                                            <Input
                                                                type="text"
                                                                placeholder="Entrer l'IMEI..."
                                                                value={imeiInput}
                                                                onChange={(e) => setImeiInput(e.target.value)}
                                                                onKeyDown={(e) => e.key === 'Enter' && handleAddByImei()}
                                                                className="font-mono"
                                                                data-testid="imei-input"
                                                            />
                                                            <Button
                                                                onClick={handleAddByImei}
                                                                disabled={addingProduct || !imeiInput.trim()}
                                                                className="bg-green-600 hover:bg-green-700"
                                                                data-testid="add-by-imei-btn"
                                                            >
                                                                {addingProduct ? (
                                                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                                                ) : (
                                                                    <Plus className="w-4 h-4" />
                                                                )}
                                                            </Button>
                                                        </div>
                                                        <p className="text-xs text-slate-400 mt-2">
                                                            Le produit sera automatiquement réservé
                                                        </p>
                                                    </>
                                                ) : (
                                                    // Bulk IMEI mode with optional prices
                                                    <>
                                                        <textarea
                                                            placeholder={"Collez vos IMEI avec prix final (optionnel):\n351942832521932 190\n352391570938446 150\n352691139654230\n\nFormats acceptés:\nIMEI ESPACE Prix (recommandé)\nIMEI;Prix ou IMEI,Prix"}
                                                            value={bulkImeiInput}
                                                            onChange={(e) => setBulkImeiInput(e.target.value)}
                                                            className="w-full h-40 px-3 py-2 text-sm font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                                            data-testid="bulk-imei-input"
                                                        />
                                                        <div className="flex items-center justify-between mt-2">
                                                            <p className="text-xs text-slate-400">
                                                                {bulkImeiInput.split(/[\n]+/).filter(i => i.trim()).length} ligne(s) détectée(s)
                                                            </p>
                                                            <Button
                                                                onClick={handleAddBulkImei}
                                                                disabled={addingBulk || !bulkImeiInput.trim()}
                                                                className="bg-green-600 hover:bg-green-700"
                                                                data-testid="add-bulk-btn"
                                                            >
                                                                {addingBulk ? (
                                                                    <>
                                                                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                                                        Ajout...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Plus className="w-4 h-4 mr-2" />
                                                                        Ajouter tout
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                        
                                                        {/* Bulk Results */}
                                                        {bulkResults && (
                                                            <div className="mt-3 p-3 bg-slate-50 rounded-lg text-xs space-y-2">
                                                                {bulkResults.added.length > 0 && (
                                                                    <div className="text-green-700">
                                                                        <Check className="w-3 h-3 inline mr-1" />
                                                                        {bulkResults.added.length} ajouté(s): {bulkResults.added.map(p => `${p.imei}${p.discount ? ` (-${p.discount}€)` : ''}`).join(', ')}
                                                                    </div>
                                                                )}
                                                                {bulkResults.errors.length > 0 && (
                                                                    <div className="text-red-600">
                                                                        <X className="w-3 h-3 inline mr-1" />
                                                                        {bulkResults.errors.length} erreur(s):
                                                                        <ul className="ml-4 mt-1">
                                                                            {bulkResults.errors.map((err, i) => (
                                                                                <li key={i}>{err.imei}: {err.error}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Quick actions */}
                                {selectedClientId && managedCart?.cart?.exists && (
                                    <Card className="border-slate-200">
                                        <CardContent className="p-4">
                                            <h4 className="font-medium text-slate-700 mb-3">Actions rapides</h4>
                                            <div className="space-y-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-full justify-start"
                                                    onClick={() => {
                                                        const clientData = {
                                                            client: managedCart.client,
                                                            client_id: selectedClientId,
                                                            item_count: managedCart.cart.item_count,
                                                            has_negotiated_discount: managedCart.cart.has_negotiated_discount
                                                        };
                                                        handleOpenDiscountModal(clientData);
                                                    }}
                                                >
                                                    <Tag className="w-4 h-4 mr-2 text-purple-600" />
                                                    Appliquer une remise
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>

                            {/* Right: Cart Contents */}
                            <div className="lg:col-span-2">
                                {!selectedClientId ? (
                                    <div className="text-center py-16 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                                        <UserPlus className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                                        <h3 className="text-lg font-semibold text-slate-700 mb-2">
                                            Sélectionnez un client
                                        </h3>
                                        <p className="text-slate-500">
                                            Choisissez un client pour créer ou modifier son panier
                                        </p>
                                    </div>
                                ) : loadingManagedCart ? (
                                    <Card className="border-slate-200">
                                        <CardContent className="p-8 text-center">
                                            <RefreshCw className="w-8 h-8 mx-auto text-slate-400 animate-spin mb-4" />
                                            <p className="text-slate-500">Chargement du panier...</p>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <Card className="border-slate-200">
                                        <CardContent className="p-0">
                                            {/* Cart Header */}
                                            <div className="p-4 border-b border-slate-200 bg-slate-50">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                            <Building2 className="w-5 h-5 text-blue-600" />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold text-slate-900">
                                                                {managedCart?.client?.company_name}
                                                            </h3>
                                                            <p className="text-sm text-slate-500">
                                                                {managedCart?.client?.email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-lg font-bold text-slate-900">
                                                            {managedCart?.cart?.total_ht?.toFixed(2)} € HT
                                                        </p>
                                                        <p className="text-sm text-slate-500">
                                                            {managedCart?.cart?.item_count || 0} article(s)
                                                        </p>
                                                    </div>
                                                </div>
                                                {managedCart?.cart?.has_negotiated_discount && (
                                                    <Badge className="mt-2 bg-purple-100 text-purple-700">
                                                        <Gift className="w-3 h-3 mr-1" />
                                                        Offre négociée active - Réservation illimitée
                                                    </Badge>
                                                )}
                                            </div>

                                            {/* Cart Items */}
                                            {!managedCart?.cart?.items?.length ? (
                                                <div className="p-8 text-center">
                                                    <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                                                    <p className="text-slate-500">Panier vide</p>
                                                    <p className="text-sm text-slate-400 mt-1">
                                                        Ajoutez des produits par IMEI
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="divide-y divide-slate-100">
                                                    {managedCart.cart.items.map((item) => (
                                                        <div 
                                                            key={item.id} 
                                                            className={`p-4 flex items-center justify-between hover:bg-slate-50 ${
                                                                !item.is_reserved ? 'bg-amber-50' : ''
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-4">
                                                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                                    <Package className="w-5 h-5 text-slate-400" />
                                                                </div>
                                                                <div>
                                                                    <p className="font-medium text-slate-900">
                                                                        {item.brand} {item.model}
                                                                    </p>
                                                                    <p className="text-sm text-slate-500">
                                                                        {item.storage_capacity} GB • {item.grade} • {item.color}
                                                                    </p>
                                                                    <p className="text-xs text-slate-400 font-mono">
                                                                        IMEI: {item.imei}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-4">
                                                                <div className="text-right">
                                                                    <p className="font-bold text-slate-900">
                                                                        {item.price?.toFixed(2)} €
                                                                    </p>
                                                                    {!item.is_reserved && (
                                                                        <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                                                                            Non réservé
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveFromManagedCart(item.id)}
                                                                    disabled={removingProduct[item.id]}
                                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                    data-testid={`remove-${item.id}`}
                                                                >
                                                                    {removingProduct[item.id] ? (
                                                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                                                    ) : (
                                                                        <Trash2 className="w-4 h-4" />
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Discount Modal */}
            <Dialog open={discountModalOpen} onOpenChange={setDiscountModalOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Calculator className="w-5 h-5 text-purple-600" />
                            {selectedClient?.has_negotiated_discount ? 'Offre Négociée' : 'Négocier une Remise'}
                            {selectedClient?.has_negotiated_discount && (
                                <Badge className="bg-purple-500 text-white ml-2">Active</Badge>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedClient?.client?.company_name} - {selectedClient?.item_count} article(s)
                        </DialogDescription>
                    </DialogHeader>

                    {loadingDetails ? (
                        <div className="py-8 text-center">
                            <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-slate-500">Chargement des détails...</p>
                        </div>
                    ) : cartDetails ? (
                        <div className="space-y-6">
                            {/* Items Table */}
                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="text-left p-3 font-semibold">Produit</th>
                                            <th className="text-right p-3 font-semibold">Prix</th>
                                            <th className="text-right p-3 font-semibold text-amber-700">Coût ERP</th>
                                            <th className="text-right p-3 font-semibold text-green-700">Marge Nette</th>
                                            <th className="text-center p-3 font-semibold text-purple-700">
                                                <div className="flex flex-col">
                                                    <span>Remise (€)</span>
                                                    <span className="text-[10px] text-green-600 font-normal">ou Prix Final <span className="text-orange-500">(HT si TVA std)</span></span>
                                                </div>
                                            </th>
                                            <th className="text-right p-3 font-semibold">Prix Final HT</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {cartDetails.items?.map((item) => {
                                            const itemDiscount = parseFloat(discounts[item.id]) || 0;
                                            const finalPrice = item.original_price - itemDiscount;
                                            const margin = finalPrice - (item.erp_cost || 0);
                                            const marginPct = finalPrice > 0 ? (margin / finalPrice * 100) : 0;
                                            const isNegativeMargin = margin < 0;
                                            
                                            // Calculate VAT based on regime (same logic as backend)
                                            const vatRegime = item.vat_regime || 'margin';
                                            let vatAmount = 0;
                                            let marginAfterVat = margin;
                                            
                                            if (vatRegime === 'standard') {
                                                vatAmount = finalPrice * 0.20;
                                                marginAfterVat = margin - vatAmount;
                                            } else {
                                                // Margin VAT: TVA = margin * 20/120
                                                vatAmount = margin > 0 ? margin * 20 / 120 : 0;
                                                marginAfterVat = margin - vatAmount;
                                            }
                                            
                                            return (
                                                <tr key={item.id} className={isNegativeMargin ? 'bg-red-50' : ''}>
                                                    <td className="p-3">
                                                        <div>
                                                            <p className="font-medium">{item.brand} {item.model}</p>
                                                            <p className="text-xs text-slate-500">{item.storage_capacity}GB • {item.color} • {item.grade}</p>
                                                            <p className="text-xs text-slate-400 font-mono">{item.imei}</p>
                                                        </div>
                                                    </td>
                                                    <td className="text-right p-3 font-medium">
                                                        {item.original_price?.toFixed(2)} €
                                                    </td>
                                                    <td className="text-right p-3 text-amber-700 font-medium">
                                                        {item.erp_cost?.toFixed(2) || '—'} €
                                                    </td>
                                                    <td className={`text-right p-3 ${isNegativeMargin ? 'text-red-600' : 'text-green-600'}`}>
                                                        <div className="flex flex-col items-end">
                                                            <span className="font-bold">
                                                                {marginAfterVat.toFixed(2)} €
                                                                <span className="text-xs ml-1 font-normal">({marginPct.toFixed(1)}%)</span>
                                                            </span>
                                                            <span className="text-xs text-slate-500 font-normal">
                                                                TVA: {vatAmount.toFixed(0)}€
                                                            </span>
                                                        </div>
                                                        {isNegativeMargin && <AlertTriangle className="w-4 h-4 inline ml-1" />}
                                                    </td>
                                                    <td className="text-center p-3">
                                                        <div className="flex flex-col items-center gap-1">
                                                            {/* Discount amount input */}
                                                            <div className="flex items-center justify-center gap-1">
                                                                <span className="text-purple-600 text-xs">-</span>
                                                                <Input
                                                                    type="number"
                                                                    min="0"
                                                                    step="1"
                                                                    value={discounts[item.id] || ''}
                                                                    onChange={(e) => setDiscounts(prev => ({
                                                                        ...prev,
                                                                        [item.id]: e.target.value ? parseFloat(e.target.value) : 0
                                                                    }))}
                                                                    className="w-20 h-7 text-center text-purple-700 font-medium text-sm"
                                                                    placeholder="0"
                                                                    title="Montant de la remise"
                                                                />
                                                                <span className="text-purple-600 text-xs">€</span>
                                                            </div>
                                                            <span className="text-[10px] text-slate-400">ou</span>
                                                            {/* Final price input */}
                                                            <div className="flex items-center justify-center gap-1">
                                                                <span className="text-green-600 text-xs">=</span>
                                                                <Input
                                                                    type="number"
                                                                    min="0"
                                                                    step="1"
                                                                    value={finalPrice > 0 && finalPrice !== item.original_price ? finalPrice.toFixed(0) : ''}
                                                                    onChange={(e) => {
                                                                        const targetPrice = e.target.value ? parseFloat(e.target.value) : item.original_price;
                                                                        const calculatedDiscount = Math.max(0, item.original_price - targetPrice);
                                                                        setDiscounts(prev => ({
                                                                            ...prev,
                                                                            [item.id]: calculatedDiscount
                                                                        }));
                                                                    }}
                                                                    className="w-20 h-7 text-center text-green-700 font-medium text-sm"
                                                                    placeholder={item.original_price?.toFixed(0)}
                                                                    title={item.vat_regime === 'standard' ? "Prix final HT" : "Prix final"}
                                                                />
                                                                <span className="text-green-600 text-xs">
                                                                    €{item.vat_regime === 'standard' && <span className="text-orange-500 font-semibold ml-0.5">HT</span>}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-right p-3 font-bold text-slate-900">
                                                        {finalPrice.toFixed(2)} €
                                                        {item.vat_regime === 'standard' && <span className="text-orange-500 text-xs font-normal ml-1">HT</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Global Discount */}
                            <div className="flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <Percent className="w-5 h-5 text-purple-600" />
                                    <span className="font-medium text-purple-900">Remise globale sur le panier</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-purple-600">-</span>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={globalDiscount || ''}
                                        onChange={(e) => setGlobalDiscount(e.target.value ? parseFloat(e.target.value) : 0)}
                                        className="w-24 h-9 text-center text-purple-700 font-medium"
                                        placeholder="0"
                                    />
                                    <span className="text-purple-600">€</span>
                                </div>
                            </div>

                            {/* Totals Summary */}
                            <div className="grid grid-cols-2 gap-4">
                                <Card className="border-slate-200">
                                    <CardContent className="p-4 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Sous-total:</span>
                                            <span className="font-medium">{totals.original.toFixed(2)} €</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Remises articles:</span>
                                            <span className="font-medium text-purple-600">-{totals.itemDiscounts.toFixed(2)} €</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Remise globale:</span>
                                            <span className="font-medium text-purple-600">-{totals.globalDiscount.toFixed(2)} €</span>
                                        </div>
                                        <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200">
                                            <span>Total remise:</span>
                                            <span className="text-purple-700">-{totals.totalDiscount.toFixed(2)} €</span>
                                        </div>
                                    </CardContent>
                                </Card>
                                
                                <Card className={`border-2 ${totals.marginAfterVat < 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
                                    <CardContent className="p-4 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Coût total ERP:</span>
                                            <span className="font-medium text-amber-700">{totals.cost.toFixed(2)} €</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Prix final HT:</span>
                                            <span className="font-bold">{totals.discounted.toFixed(2)} €</span>
                                        </div>
                                        <div className={`flex justify-between text-lg font-bold pt-2 border-t ${
                                            totals.marginAfterVat < 0 ? 'border-red-200' : 'border-green-200'
                                        }`}>
                                            <span className="flex items-center gap-2">
                                                <TrendingDown className={`w-5 h-5 ${totals.marginAfterVat < 0 ? 'text-red-600' : 'text-green-600'}`} />
                                                Marge nette:
                                            </span>
                                            <div className="text-right">
                                                <span className={totals.marginAfterVat < 0 ? 'text-red-700' : 'text-green-700'}>
                                                    {totals.marginAfterVat.toFixed(2)} € ({totals.marginPct.toFixed(1)}%)
                                                </span>
                                                <span className="block text-xs text-slate-500 font-normal">
                                                    TVA: {totals.totalVat.toFixed(0)} €
                                                </span>
                                            </div>
                                        </div>
                                        {totals.marginAfterVat < 0 && (
                                            <p className="text-xs text-red-600 flex items-center gap-1 mt-2">
                                                <AlertTriangle className="w-4 h-4" />
                                                Attention: marge négative !
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Info about negotiated discount */}
                            {!selectedClient?.has_negotiated_discount && totals.totalDiscount > 0 && (
                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-sm text-blue-800">
                                        <strong>Note:</strong> En appliquant cette remise, la réservation deviendra 
                                        <strong> illimitée</strong> (le client aura tout le temps pour finaliser).
                                        Vous pourrez annuler l&apos;offre à tout moment.
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-center py-8 text-slate-500">Aucune donnée disponible</p>
                    )}

                    <DialogFooter className="flex gap-2 pt-4">
                        {selectedClient?.has_negotiated_discount && (
                            <Button
                                variant="destructive"
                                onClick={handleCancelOffer}
                                disabled={cancellingOffer}
                                className="mr-auto"
                            >
                                <Ban className="w-4 h-4 mr-2" />
                                {cancellingOffer ? 'Annulation...' : 'Annuler l\'offre'}
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => setDiscountModalOpen(false)}>
                            <X className="w-4 h-4 mr-2" />
                            Fermer
                        </Button>
                        <Button 
                            onClick={handleApplyDiscount}
                            disabled={applyingDiscount || totals.totalDiscount === 0}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            <Check className="w-4 h-4 mr-2" />
                            {applyingDiscount ? 'Application...' : 
                                selectedClient?.has_negotiated_discount ? 'Mettre à jour' : 'Valider la remise'
                            }
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Relaunch Result Modal */}
            <Dialog open={relaunchModalOpen} onOpenChange={setRelaunchModalOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {relaunchResult?.success ? (
                                <>
                                    <Check className="w-5 h-5 text-green-600" />
                                    <span className="text-green-700">Panier relancé</span>
                                </>
                            ) : (
                                <>
                                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                                    <span className="text-amber-700">Relancement partiel</span>
                                </>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            {relaunchResult?.clientInfo?.company_name} - {relaunchResult?.clientInfo?.email}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Success Summary */}
                        {relaunchResult?.reserved_count > 0 && (
                            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                <h4 className="font-semibold text-green-800 mb-2">
                                    ✅ {relaunchResult.reserved_count} produit(s) réservé(s) pour 10 minutes
                                </h4>
                                <p className="text-sm text-green-600 mb-2">
                                    Total: {relaunchResult.total_ht?.toFixed(2)} € HT
                                </p>
                                <div className="space-y-1">
                                    {relaunchResult.reserved_products?.map((product, idx) => (
                                        <div key={idx} className="text-sm text-green-700 flex justify-between">
                                            <span>{product.brand} {product.model}</span>
                                            <span className="font-medium">{product.price?.toFixed(2)} €</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Unavailable Products */}
                        {relaunchResult?.unavailable_count > 0 && (
                            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                                <h4 className="font-semibold text-amber-800 mb-2">
                                    ⚠️ {relaunchResult.unavailable_count} produit(s) non disponible(s) - retirés du panier
                                </h4>
                                <div className="space-y-2">
                                    {relaunchResult.unavailable_products?.map((product, idx) => (
                                        <div key={idx} className="text-sm">
                                            <span className="text-amber-700 font-medium">
                                                {product.brand} {product.model}
                                            </span>
                                            <span className="text-amber-600 ml-2">
                                                ({product.reason})
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* No products available */}
                        {!relaunchResult?.success && relaunchResult?.reserved_count === 0 && (
                            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                                <h4 className="font-semibold text-red-800">
                                    ❌ Aucun produit disponible pour réservation
                                </h4>
                                <p className="text-sm text-red-600 mt-1">
                                    Tous les produits de ce panier sont indisponibles ou déjà réservés.
                                </p>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button onClick={() => setRelaunchModalOpen(false)}>
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default AdminReservations;
